import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { logout, refreshTokens } from '../features/auth/authSlice';
import { toastService } from '../utils/toastService';
import { formatErrorMessage } from '../utils/errorUtils';
import { convertDatesToUTC, hasDateFields } from '../utils/dateUtils';
import { getEnv } from '../config/runtimeConfig';
import i18n from '../i18n';

// Configuration
const config = {
    logging: {
        enabled: getEnv('REACT_APP_ENABLE_DEBUG_LOGGING') === 'true' || false,
        level: 'INFO', // ERROR, WARN, INFO, DEBUG
        showRequestDetails: false,
        showResponseBody: true,
        combineRequestResponse: true
    },
    urls: {
        api: getEnv('REACT_APP_API_URL'),
        auth: getEnv('REACT_APP_AUTH_URL') || getEnv('REACT_APP_API_URL')
    }
};

// Log initial configuration
// console.log('API URL:', config.urls.api);
// console.log('Auth URL:', config.urls.auth);
// console.log('Debug logging:', config.logging.enabled);

// Enable runtime toggle via browser console
if (typeof window !== 'undefined') {
    window.toggleApiLogging = (enable) => {
        config.logging.enabled = !!enable;
        return `API logging is now ${config.logging.enabled ? 'enabled' : 'disabled'}`;
    };
}

// Logger with level support
const logger = {
    LEVELS: { ERROR: 0, WARN: 1, INFO: 2, DEBUG: 3 },

    log(level, message, data) {
        if (!config.logging.enabled) return;

        const levelValue = this.LEVELS[level.toUpperCase()] ?? this.LEVELS.DEBUG;
        const currentLevel = this.LEVELS[config.logging.level] ?? this.LEVELS.INFO;

        if (levelValue > currentLevel) return;

        const method = console[level.toLowerCase()] || console.log;
        data !== undefined ? method(`🔄 ${message}:`, data) : method(`🔄 ${message}`);
    }
};

// URL and request utilities
const urlUtils = {
    isAuthRequest: (url) => {
        if (!url) return false;
        return url.includes('/auth/') || url.includes('/v1/auth/') || url.startsWith('/token') || url.startsWith('/refresh') || url.startsWith('/revoke') || url.startsWith('/validate');
    },

    // Identity API'nin yetki yönetimi uçları (rol, yetki matrisi, kullanıcı rolleri) auth ile aynı sunucudadır
    isIdentityManagementRequest: (url) => {
        if (!url) return false;
        return /^\/?v1\/(permission|role)(\/|\?|$)/i.test(url);
    },

    getBaseUrl: (url) => {
        return urlUtils.isAuthRequest(url) || urlUtils.isIdentityManagementRequest(url) ? config.urls.auth : config.urls.api;
    },

    // Normalize path to ensure it works with URL constructor
    normalizePath: (path) => (path?.startsWith('/') ? path.substring(1) : path),

    // Add query parameters to URL
    addQueryParams: (url, params = {}) => {
        if (!params || Object.keys(params).length === 0) return url;

        try {
            const urlObj = new URL(url);
            Object.entries(params).forEach(([key, value]) => {
                if (value != null) urlObj.searchParams.append(key, String(value));
            });
            return urlObj.toString();
        } catch (error) {
            console.error('Error adding query parameters:', error);
            return url;
        }
    },

    // Build a complete URL from parts
    buildUrl: (input, path = '', baseUrl = null) => {
        // Handle different input types elegantly
        if (typeof input === 'string') {
            return new URL(urlUtils.normalizePath(path), input).toString();
        }

        if (input instanceof Request) {
            const base = input.url.endsWith('/') ? input.url : `${input.url}/`;
            return new URL(urlUtils.normalizePath(path), base).toString();
        }

        if (input?.url) {
            const base = input.url.endsWith('/') ? input.url : `${input.url}/`;
            return new URL(urlUtils.normalizePath(path), base).toString();
        }

        // Default case: use the appropriate base URL
        baseUrl = baseUrl || urlUtils.getBaseUrl(path);
        return new URL(urlUtils.normalizePath(path), baseUrl).toString();
    }
};

// Aynı anda düşen birden fazla 403 için tek uyarı
const FORBIDDEN_TOAST_INTERVAL_MS = 3000;
let lastForbiddenToastAt = 0;

const notifyForbidden = (error) => {
    const now = Date.now();
    if (now - lastForbiddenToastAt < FORBIDDEN_TOAST_INTERVAL_MS) return;
    lastForbiddenToastAt = now;
    const detail = error?.data?.errorMessage || i18n.t('common.forbiddenAction');
    toastService.warn(detail, i18n.t('common.warning'), 6000);
};

// HTTP status text dictionary
const HTTP_STATUS = {
    200: 'OK',
    201: 'Created',
    204: 'No Content',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout'
};

/**
 * Enhanced fetch with logging, error handling, and performance metrics
 * (Currently unused - kept for reference)
 */
const enhancedFetch = async (input, init = {}, args = {}) => {
    // Extract and normalize parameters
    const method = init?.method || args.method || 'GET';
    const path = args.url || '';

    // Build base URL
    let url = urlUtils.buildUrl(input, path);

    // Create fetch request input
    let requestInput;
    if (typeof input === 'string') {
        requestInput = url;
    } else if (input instanceof Request) {
        requestInput = new Request(url, input);
    } else if (input?.url) {
        requestInput = { ...input, url };
    } else {
        requestInput = url;
    }

    // Add query parameters if present
    if (args.params && Object.keys(args.params).length > 0) {
        url = urlUtils.addQueryParams(url, args.params);

        // Update requestInput with new URL containing query params
        if (typeof requestInput === 'string') {
            requestInput = url;
        } else if (requestInput instanceof Request) {
            requestInput = new Request(url, requestInput);
        } else if (requestInput && typeof requestInput === 'object') {
            requestInput.url = url;
        }
    }

    // Configure fetch options
    const fetchOptions = {
        ...init,
        method,
        credentials: 'include',
        headers: { ...(init.headers || {}) }
    };

    // Add body if present
    if (args.body && !init.body) {
        if (args.body instanceof FormData) {
            fetchOptions.body = args.body;
            // ❗ Do NOT set Content-Type here
        } else {
            fetchOptions.body = typeof args.body === 'string' ? args.body : JSON.stringify(args.body);

            // ✅ Sadece JSON için Content-Type ekle
            if (!fetchOptions.headers['Content-Type']) {
                fetchOptions.headers['Content-Type'] = 'application/json';
            }
        }
    }

    // Request logging
    if (config.logging.showRequestDetails) {
        logger.log('debug', `Request (${method})`, {
            url: requestInput,
            method,
            body: args.body || init.body
        });
    }

    // Track performance
    const startTime = Date.now();

    try {
        // Execute the request
        const response = await fetch(requestInput, fetchOptions);
        const status = response?.status;
        const statusText = HTTP_STATUS[status] || 'Unknown';
        const duration = Date.now() - startTime;

        // Log response status
        const logLevel = status >= 400 ? 'error' : 'info';

        if (config.logging.combineRequestResponse) {
            logger.log(logLevel, `${method} ${url} → ${status} ${statusText} (${duration}ms)`, {
                status,
                url: response?.url,
                duration
            });
        } else {
            logger.log(logLevel, `Response (${status} - ${statusText})`, {
                status,
                url: response?.url,
                duration
            });
        }

        // Log response body if configured
        if (config.logging.showResponseBody && config.logging.enabled) {
            try {
                const clonedResponse = response.clone();
                const text = await clonedResponse.text();

                try {
                    const json = JSON.parse(text);
                    logger.log('debug', `Response JSON (${method} - ${status})`, json);
                } catch {
                    logger.log('debug', `Response Text (${method} - ${status})`, text);
                }
            } catch (error) {
                logger.log('warn', 'Could not read response body', error.message);
            }
        }

        return response;
    } catch (error) {
        logger.log('error', `Request failed: ${method} ${url}`, error);
        throw error;
    }
};

/**
 * Create a base query with auth headers
 */
export const baseQuery = async (args, api, extraOptions) => {
    // Date conversion
    if (args.body && hasDateFields(args.body)) {
        args.body = convertDatesToUTC(args.body);
    }

    if (args.params && hasDateFields(args.params)) {
        args.params = convertDatesToUTC(args.params);
    }

    if (args.params) {
        args.params = Object.fromEntries(
            Object.entries(args.params).filter(([, v]) => v != null)
        );
    }

    const baseUrl = urlUtils.getBaseUrl(args.url);
    logger.log('info', `Using base URL: ${baseUrl} for ${args.url} (${args.method || 'GET'})`);

    // Create fetchBaseQuery instance with correct baseUrl
    const fetchQuery = fetchBaseQuery({
        baseUrl,
        prepareHeaders: async (headers, { getState, endpoint, extra } = {}) => {
            if (!headers.has('Content-Type') && args.body && !(args.body instanceof FormData)) {
                headers.set('Content-Type', 'application/json');
            }

            try {
                const savedLanguage = localStorage.getItem('selectedLanguage');
                let locale = 'US';

                if (savedLanguage) {
                    const languageData = JSON.parse(savedLanguage);
                    const localeMap = { TR: 'TR', US: 'US', DE: 'DE', FR: 'FR', IT: 'IT', RO: 'RO' };
                    locale = localeMap[languageData.code] || 'US';
                }

                headers.set('X-App-Language', locale);
            } catch (error) {
                headers.set('X-App-Language', 'US');
            }

            // Redux state'den veri almak için - güvenli kontrol
            if (typeof getState === 'function') {
                try {
                    const state = getState();
                    if (state?.auth?.csrfToken) {
                        headers.set('X-CSRF-Token', state.auth.csrfToken);
                    }
                } catch (error) {
                    console.warn('getState error:', error);
                }
            }

            return headers;
        },
        credentials: 'include'
    });

    // Call with args (including responseHandler if present)
    return await fetchQuery(args, api, extraOptions);
};
/**
 * Refresh Token Manager - Prevents race conditions and handles concurrent refresh requests
 */
class RefreshTokenManager {
    constructor() {
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.failedQueue = [];
        this.lastRefreshTime = 0;
        this.refreshCooldown = 5000; // 5 seconds cooldown between refreshes
    }

    /**
     * Process queued requests after refresh completes
     */
    processQueue(error, result) {
        this.failedQueue.forEach(({ resolve, reject }) => {
            if (error) {
                reject(error);
            } else {
                resolve(result);
            }
        });

        this.failedQueue = [];
    }

    /**
     * Handle refresh with queue management and cooldown
     */
    async handleRefresh(api, extraOptions) {
        const now = Date.now();

        // Check cooldown period to prevent rapid refresh attempts
        if (now - this.lastRefreshTime < this.refreshCooldown) {
            logger.log('warn', 'Refresh attempt blocked by cooldown period');
            return { success: false, reason: 'cooldown' };
        }

        // If already refreshing, queue this request
        if (this.isRefreshing && this.refreshPromise) {
            logger.log('info', 'Refresh already in progress, queuing request');
            return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
            });
        }

        // Start refreshing
        this.isRefreshing = true;
        this.lastRefreshTime = now;
        this.refreshPromise = this.performRefresh(api, extraOptions);

        try {
            const result = await this.refreshPromise;
            this.processQueue(null, result);
            return result;
        } catch (error) {
            this.processQueue(error, null);
            throw error;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    /**
     * Actual refresh implementation
     */
    async performRefresh(api, extraOptions) {
        logger.log('info', 'Cookie-based auth expired, attempting refresh');

        const { dispatch } = api || {};

        try {
            // Try token refresh - cookies will be sent automatically
            const refreshResult = await baseQuery(
                {
                    url: '/v1/auth/refresh',
                    method: 'POST',
                    body: {} // Empty body for cookie-based refresh
                },
                api,
                { ...extraOptions, skipAuthHeader: true }
            );

            if (refreshResult?.data && refreshResult.data.authenticated) {
                // Update frontend state with new CSRF token and auth status
                dispatch?.(
                    refreshTokens({
                        token: 'cookie-based-auth',
                        refreshToken: 'cookie-based-refresh',
                        csrfToken: refreshResult.data.csrfToken
                    })
                );

                logger.log('info', 'Token refresh successful');
                return { success: true };
            }

            // Refresh failed but no error thrown
            logger.log('warn', 'Refresh response indicates failure');
            return { success: false, reason: 'invalid_response' };
        } catch (error) {
            logger.log('error', 'Refresh request failed', error);
            return { success: false, reason: 'request_failed', error };
        }
    }

    /**
     * Force logout with cleanup
     */
    forceLogout(api) {
        const { dispatch } = api || {};

        // Clear any pending refreshes
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.processQueue(new Error('Forced logout'), null);

        // Perform logout
        dispatch?.(logout());
        toastService.warn('Oturum yenilenemedi. Lütfen tekrar giriş yapın.', 'Uyarı');

        logger.log('info', 'Forced logout completed');
    }
}

// Global refresh token manager instance
const refreshTokenManager = new RefreshTokenManager();

/**
 * Handle token refresh when authentication fails (Cookie-based auth)
 */
const handleTokenRefresh = async (api, extraOptions) => {
    try {
        const result = await refreshTokenManager.handleRefresh(api, extraOptions);

        if (!result.success) {
            // Handle different failure reasons
            if (result.reason === 'cooldown') {
                logger.log('warn', 'Refresh blocked by cooldown, treating as temporary failure');
                return { success: false };
            } else {
                // Force logout for other failures
                refreshTokenManager.forceLogout(api);
                return { success: false };
            }
        }

        return result;
    } catch (error) {
        logger.log('error', 'Token refresh handler error', error);
        refreshTokenManager.forceLogout(api);
        return { success: false };
    }
};

/**
 * Enhanced query with token refresh capability and improved error handling
 */
export const baseQueryWithReauth = async (args, api, extraOptions = {}) => {
    const method = args.method || 'GET';
    const url = typeof args.url === 'string' ? args.url : 'unknown endpoint';

    logger.log('info', `API Request: ${method} ${url}`, config.logging.showRequestDetails ? args : null);

    try {
        // Attempt the initial query
        let result = await baseQuery(args, api, extraOptions);

        // Handle errors
        if (result?.error) {
            const errorStatus = result.error.status || 'UNKNOWN';

            // Only log error details if not already logged
            if (!config.logging.combineRequestResponse) {
                logger.log('error', `Request failed: ${method} ${url} (${errorStatus})`, result.error);
            }

            // Check both direct status and PARSING_ERROR with originalStatus
            // (PARSING_ERROR occurs when backend returns non-JSON response like HTML for expired sessions)
            const httpStatus = result.error.status === 'PARSING_ERROR' 
                ? result.error.originalStatus 
                : result.error.status;

            // Show error message to user (403: DMS gövdesi boş döner, tek bir yetki uyarısı gösterilir)
            if (extraOptions?.silentError) {
                // Çağıran taraf hatayı kendisi yönetiyor
            } else if (httpStatus === 403) {
                notifyForbidden(result.error);
            } else {
                toastService.error(formatErrorMessage(result.error), 'Hata', 6000);
            }

            // Handle 401 errors with token refresh
            if (httpStatus === 401) {
                const refreshResult = await handleTokenRefresh(api, extraOptions);

                if (refreshResult.success) {
                    logger.log('info', 'Token refresh successful, retrying original request');
                    result = await baseQuery(args, api, extraOptions);
                }
            }

            // Enhance error object with user-friendly message
            if (result?.error) {
                result.error.message = 'An error occurred during the operation.';
                if (result.error.data?.errorMessage) {
                    result.error.errorMessage = result.error.data.errorMessage;
                }
            }
        }

        return (
            result || {
                error: {
                    status: 'UNKNOWN_ERROR',
                    message: 'An error occurred during the operation.',
                    errorMessage: null
                }
            }
        );
    } catch (error) {
        // Handle unexpected errors
        logger.log('error', `API request handler error: ${method} ${url}`, error);
        toastService.error('Beklenmeyen bir hata oluştu. Lütfen daha sonra tekrar deneyin.', 'Hata', 6000);

        return {
            error: {
                status: 'FETCH_ERROR',
                error: error.toString(),
                message: 'An error occurred during the operation.',
                errorMessage: error.message || null,
                data: null
            }
        };
    }
};
